import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { projectService, clientService, employeeService } from "../services/apiService.jsx";
import "./Dashboard.css";

const UpdateProject = () => {
  const { id } = useParams(); // Get project ID from URL
  const navigate = useNavigate();
  const formRef = useRef();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState(""); // 'success' or 'error'
  const [notificationMessage, setNotificationMessage] = useState("");
  
  // State for customers dropdown
  const [customers, setCustomers] = useState([]);
  // State for employees dropdown
  const [employees, setEmployees] = useState([]);
  const [formLoading, setFormLoading] = useState(true);
  
  // State for form data - matching the API expected field names
  const [formData, setFormData] = useState({
    project_name: "",
    customer_id: "",
    employee_id: "",
    project_start_date: "",
    project_end_date: "",
    project_type: "",
    project_description: "",
    project_priority: "Medium",
    project_status: "Active"
  });

  // State for showing delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Fetch project details, customers, and employees on component mount
  useEffect(() => {
    if (id) {
      fetchProjectDetails();
      fetchFormData();
    }
  }, [id]);

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const showNotificationMessage = (type, message) => {
    setNotificationType(type);
    setNotificationMessage(message);
    setShowNotification(true);
  };

  const fetchProjectDetails = async () => {
    setLoading(true);
    try {
      const response = await projectService.getProjectById(id);
      console.log("Project Details Response:", response);

      // Handle different response structures
      let projectData = null;

      if (response?.data?.data) {
        projectData = response.data.data;
      } else if (response?.data) {
        projectData = response.data;
      } else if (response?.success && response?.data) {
        projectData = response.data;
      }

      if (projectData) {
        // Format dates for input fields (YYYY-MM-DD)
        const formatDate = (dateString) => {
          if (!dateString) return "";
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";
          return date.toISOString().split("T")[0];
        };

        setFormData({
          project_name: projectData.project_name || "",
          customer_id: projectData.customer_id || "",
          employee_id: projectData.employee_id || "",
          project_start_date: formatDate(projectData.project_start_date),
          project_end_date: formatDate(projectData.project_end_date),
          project_type: projectData.project_type || "",
          project_description: projectData.project_description || "",
          project_priority: projectData.project_priority || "Medium",
          project_status: projectData.project_status || "Active"
        });
      } else {
        showNotificationMessage("error", "Failed to load project details. No data found.");
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
      showNotificationMessage("error", "Failed to load project details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

const fetchFormData = async () => {
  setFormLoading(true);
  try {
    // Fetch customers and employees in parallel
    const [customersResponse, employeesResponse] = await Promise.all([
      clientService.getAllClients(),
      employeeService.getAllEmployees()
    ]);

    console.log("Customers Response:", customersResponse);
    console.log("Employees Response:", employeesResponse);

    // Extract customers data
    let customersData = [];
    if (customersResponse?.data) {
      if (Array.isArray(customersResponse.data)) {
        customersData = customersResponse.data;
      } else if (customersResponse.data?.data && Array.isArray(customersResponse.data.data)) {
        customersData = customersResponse.data.data;
      }
    }
    setCustomers(customersData);

    // Extract employees data - FIXED: Access the nested data array
    let employeesData = [];
    if (employeesResponse?.data?.data && Array.isArray(employeesResponse.data.data)) {
      employeesData = employeesResponse.data.data;
      console.log("Employees data loaded:", employeesData.length);
    }
    
    setEmployees(employeesData);
    
  } catch (error) {
    console.error("Error fetching form data:", error);
    showNotificationMessage("error", "Failed to load customers and employees.");
  } finally {
    setFormLoading(false);
  }
};

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Only Project Name is mandatory
    if (!formData.project_name) errors.project_name = "Project name is required";
    
    // Optional field validations
    if (formData.project_start_date && formData.project_end_date) {
      const startDate = new Date(formData.project_start_date);
      const endDate = new Date(formData.project_end_date);
      if (endDate < startDate) {
        errors.project_end_date = "End date cannot be before start date";
      }
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      showNotificationMessage("error", "Please fix the validation errors");
      return;
    }
    
    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      // Prepare data for API
      const projectData = {
        project_name: formData.project_name,
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        employee_id: formData.employee_id ? parseInt(formData.employee_id) : null,
        project_start_date: formData.project_start_date || null,
        project_end_date: formData.project_end_date || null,
        project_type: formData.project_type || null,
        project_description: formData.project_description || null,
        project_priority: formData.project_priority || "Medium",
        project_status: formData.project_status || "Active"
      };

      console.log("Sending update data:", projectData);

      const response = await projectService.updateProject(id, projectData);
      console.log("Update Project Response:", response);
      
      showNotificationMessage("success", "Project updated successfully!");
      setSuccess("Project updated successfully!");
      
      setTimeout(() => {
        navigate("/myprojects");
      }, 2000);
      
    } catch (error) {
      console.error("Error updating project:", error);
      
      if (error.response) {
        if (error.response.status === 400) {
          const errorData = error.response.data;
          if (errorData.errors) {
            setValidationErrors(errorData.errors);
            showNotificationMessage("error", "Validation failed. Please check the form.");
          } else {
            showNotificationMessage("error", errorData.message || "Validation error");
          }
        } else if (error.response.status === 401) {
          showNotificationMessage("error", "You are not authorized. Please login again.");
        } else if (error.response.status === 403) {
          showNotificationMessage("error", "You don't have permission to update projects.");
        } else if (error.response.status === 404) {
          showNotificationMessage("error", "Project not found.");
        } else {
          showNotificationMessage("error", error.response.data?.message || "Failed to update project");
        }
      } else if (error.request) {
        showNotificationMessage("error", "No response from server. Please check your internet connection.");
      } else {
        showNotificationMessage("error", "An error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);
    setIsDeleting(true);

    try {
      const response = await projectService.deleteProject(id);
      console.log("Delete Project Response:", response);
      
      showNotificationMessage("success", "Project deleted successfully!");
      
      setTimeout(() => {
        navigate("/myprojects");
      }, 2000);
      
    } catch (error) {
      console.error("Error deleting project:", error);
      
      if (error.response) {
        if (error.response.status === 401) {
          showNotificationMessage("error", "You are not authorized. Please login again.");
        } else if (error.response.status === 403) {
          showNotificationMessage("error", "You don't have permission to delete projects.");
        } else if (error.response.status === 404) {
          showNotificationMessage("error", "Project not found.");
        } else {
          showNotificationMessage("error", error.response.data?.message || "Failed to delete project");
        }
      } else if (error.request) {
        showNotificationMessage("error", "No response from server. Please check your internet connection.");
      } else {
        showNotificationMessage("error", "An error occurred. Please try again.");
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  // Project type options
  const projectTypes = [
    "Software Development",
    "Web Development",
    "Mobile App Development",
    "UI/UX Design",
    "Quality Assurance",
    "DevOps",
    "Consulting",
    "Maintenance",
    "Research",
    "Other"
  ];

  // Priority options
  const priorityOptions = [
    "High",
    "Medium",
    "Low"
  ];

  // Status options - must match backend's updateProject validation exactly
  // (validation.js schemas.updateProject), or saving a status not in that
  // list gets rejected with a 400.
  const statusOptions = [
    "Active",
    "Inactive",
    "Completed",
    "On Hold",
    "Cancelled"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="d-flex align-items-center" style={{ marginBottom: '0.5rem', marginTop: '0.25rem' }}>
          <button
            onClick={() => navigate("/myprojects")}
            className="btn btn-link text-dark p-0 me-2"
            style={{ fontSize: '1.6rem', lineHeight: 1, textDecoration: 'none' }}
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 500 }}>Update Project</h2>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading project details...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Notification Popup */}
      {showNotification && (
        <div 
          className={`notification-popup ${notificationType === 'success' ? 'notification-success' : 'notification-error'}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            padding: '12px 16px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: notificationType === 'success' ? '#d4edda' : '#f8d7da',
            color: notificationType === 'success' ? '#155724' : '#721c24',
            border: `1px solid ${notificationType === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            minWidth: '280px',
            animation: 'slideIn 0.3s ease',
            fontSize: '0.9rem'
          }}
        >
          <i className={`bi ${notificationType === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`} style={{ fontSize: '1rem' }}></i>
          <span style={{ flex: 1 }}>{notificationMessage}</span>
          <button
            onClick={() => setShowNotification(false)}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.1rem',
              cursor: 'pointer',
              color: notificationType === 'success' ? '#155724' : '#721c24',
              padding: '0 3px'
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000
          }}
          onClick={handleDeleteCancel}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '6px',
              maxWidth: '350px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#f8d7da',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: '#dc3545', fontSize: '18px' }}></i>
                </div>
                <h4 style={{ margin: 0, color: '#dc3545', fontSize: '1.1rem' }}>Confirm Delete</h4>
              </div>
            </div>
            
            <p style={{ marginBottom: '20px', fontSize: '14px', lineHeight: '1.4' }}>
              Are you sure you want to delete project <strong>"{formData.project_name}"</strong>? This action cannot be undone.
            </p>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={handleDeleteCancel}
                style={{
                  padding: '6px 16px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{
                  padding: '6px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '13px',
                  opacity: isDeleting ? 0.7 : 1
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add animation keyframes */}
      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      
      {/* Header with back button, title and delete button */}
      <div className="d-flex align-items-center justify-content-between" style={{ marginBottom: '0.5rem', marginTop: '0.25rem' }}>
        <div className="d-flex align-items-center">
          <button
            onClick={() => navigate("/myprojects")}
            className="btn btn-link text-dark p-0 me-2"
            style={{ fontSize: '1.6rem', lineHeight: 1, textDecoration: 'none' }}
            title="Go back to projects list"
          >
            <i className="bi bi-arrow-left"></i>
          </button>
          <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 500 }}>Update Project</h2>
        </div>
        <button
          onClick={handleDeleteClick}
          className="btn btn-danger btn-sm"
          style={{ padding: '0.2rem 0.8rem', fontSize: '0.8rem' }}
          disabled={isDeleting}
        >
          <i className="bi bi-trash me-1"></i>
          Delete Project
        </button>
      </div>
      
      {/* Project name subtitle */}
      <p className="text-muted" style={{ marginBottom: '0.75rem', fontSize: '0.9rem' }}>
        Updating project: <strong>{formData.project_name}</strong>
      </p>
      
      <section className="section" style={{ padding: 0 }}>
        <div className="row" style={{ margin: 0 }}>
          <div className="col-lg-12" style={{ padding: 0 }}>
            <div className="card" style={{ marginTop: 0, border: '1px solid #dee2e6', borderRadius: '6px' }}>
              <div className="card-body" style={{ padding: '0.75rem' }}>
                <form
                  className="row g-1"
                  id="projectForm"
                  ref={formRef}
                  noValidate
                  onSubmit={handleSubmit}
                >
                  {/* Project Name - Required */}
                  <div className="col-md-3">
                    <label htmlFor="projectName" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Project Name <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="project_name"
                      className={`form-control form-control-sm ${validationErrors.project_name ? 'is-invalid' : ''}`}
                      id="projectName"
                      placeholder="Enter project name"
                      value={formData.project_name}
                      onChange={handleInputChange}
                      required
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                    {validationErrors.project_name && (
                      <div className="invalid-feedback" style={{ fontSize: '0.7rem' }}>{validationErrors.project_name}</div>
                    )}
                  </div>
                  
                  {/* Customer Dropdown */}
                  <div className="col-md-3">
                    <label htmlFor="customerSelect" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Customer
                    </label>
                    <select
                      name="customer_id"
                      className={`form-select form-select-sm ${validationErrors.customer_id ? 'is-invalid' : ''}`}
                      id="customerSelect"
                      value={formData.customer_id}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    >
                      <option value="">Select Customer</option>
                      {customers.length > 0 ? (
                        customers.map((customer) => (
                          <option key={customer.customer_id || customer.id} value={customer.customer_id || customer.id}>
                            {customer.customer_name || customer.name || `Customer ${customer.customer_id}`}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No customers available</option>
                      )}
                    </select>
                  </div>
                  
                  {/* Employee Dropdown (Project Manager) */}
                  <div className="col-md-3">
                    <label htmlFor="employeeSelect" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Project Manager
                    </label>
                    <select
                      name="employee_id"
                      className={`form-select form-select-sm ${validationErrors.employee_id ? 'is-invalid' : ''}`}
                      id="employeeSelect"
                      value={formData.employee_id}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    >
                      <option value="">Select Employee</option>
                      {employees.length > 0 ? (
                        employees.map((employee) => (
                          <option key={employee.employee_id || employee.id} value={employee.employee_id || employee.id}>
                            {employee.first_name} {employee.last_name} - {employee.employee_code || ''}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No employees available</option>
                      )}
                    </select>
                  </div>
                  
                  {/* Project Type Dropdown */}
                  <div className="col-md-3">
                    <label htmlFor="projectType" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Project Type
                    </label>
                    <select
                      name="project_type"
                      className={`form-select form-select-sm ${validationErrors.project_type ? 'is-invalid' : ''}`}
                      id="projectType"
                      value={formData.project_type}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    >
                      <option value="">Select Type</option>
                      {projectTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Start Date */}
                  <div className="col-md-3">
                    <label htmlFor="startDate" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="project_start_date"
                      className={`form-control form-control-sm ${validationErrors.project_start_date ? 'is-invalid' : ''}`}
                      id="startDate"
                      value={formData.project_start_date}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                  </div>
                  
                  {/* End Date */}
                  <div className="col-md-3">
                    <label htmlFor="endDate" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      name="project_end_date"
                      className={`form-control form-control-sm ${validationErrors.project_end_date ? 'is-invalid' : ''}`}
                      id="endDate"
                      value={formData.project_end_date}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    />
                    {validationErrors.project_end_date && (
                      <div className="invalid-feedback" style={{ fontSize: '0.7rem' }}>{validationErrors.project_end_date}</div>
                    )}
                  </div>
                  
                  {/* Priority Dropdown */}
                  <div className="col-md-3">
                    <label htmlFor="priority" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Priority
                    </label>
                    <select
                      name="project_priority"
                      className={`form-select form-select-sm ${validationErrors.project_priority ? 'is-invalid' : ''}`}
                      id="priority"
                      value={formData.project_priority}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    >
                      {priorityOptions.map((priority, index) => (
                        <option key={index} value={priority}>{priority}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Status Dropdown */}
                  <div className="col-md-3">
                    <label htmlFor="status" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Status
                    </label>
                    <select
                      name="project_status"
                      className={`form-select form-select-sm ${validationErrors.project_status ? 'is-invalid' : ''}`}
                      id="status"
                      value={formData.project_status}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    >
                      {statusOptions.map((status, index) => (
                        <option key={index} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Description - Full width */}
                  <div className="col-md-12">
                    <label htmlFor="description" className="form-label" style={{ fontSize: '0.8rem', marginBottom: '0.15rem', fontWeight: 500 }}>
                      Project Description
                    </label>
                    <textarea
                      name="project_description"
                      className={`form-control form-control-sm ${validationErrors.project_description ? 'is-invalid' : ''}`}
                      id="description"
                      rows="2"
                      placeholder="Enter project description"
                      value={formData.project_description}
                      onChange={handleInputChange}
                      style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem' }}
                    ></textarea>
                  </div>
                  
                  {/* Submit and Cancel Buttons - Right aligned */}
                  <div className="col-12 d-flex justify-content-end gap-2" style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => navigate("/myprojects")}
                      style={{ padding: '0.2rem 0.8rem', fontSize: '0.8rem' }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary"
                      disabled={isSubmitting}
                      style={{ padding: '0.2rem 0.8rem', fontSize: '0.8rem' }}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" style={{ width: '0.8rem', height: '0.8rem' }}></span>
                          Updating...
                        </>
                      ) : (
                        "Update Project"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default UpdateProject;